import useSimpleDialog from '@app/hooks/use-simple-dialog';

import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import {
  FilterOptions,
  useServerFilterOptions,
} from '@app/components/data-table/hooks/use-server-filter-options';
import { DataTableProps } from '@app/components/data-table/types/data-table';
import {
  PaginatedStationaryCombustionActivitiesListList,
  StationaryCombustionActivitiesList,
} from '@shared/api/types';
import AddDataMenu from '@app/components/imports/scope-1-2-import/components/add-data-menu';
import { baseTableSchema } from '@shared/components/data-table/schema';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { useCCF } from '../../ccf-context';
import StationaryCombustionEditForm from './stationary-combustion-edit-form';
import StationaryCombustionPreviewSheet from './stationary-combustion-preview-sheet';
import { useStationaryCombustionTableColumns } from './stationary-combustion-table-columns';

export const schema = baseTableSchema.extend({
  uuid: z.string().optional(),
});

interface StationaryCombustionTableProps
  extends DataTableProps<PaginatedStationaryCombustionActivitiesListList> {}

export default function StationaryCombustionTable({
  data,
  isLoading,
  isPreviousData,
  isFetchedAfterMount,
  status,
}: StationaryCombustionTableProps) {
  const { scope3Supported } = useCCF();
  const [perPage] = useQueryState('perPage', parseAsInteger.withDefault(10));

  const [selectedActivityUuid, setSelectedActivityUuid] =
    useState<StationaryCombustionActivitiesList['uuid']>();
  const {
    showSimpleDialog: showStationaryCombustionDialog,
    SimpleDialogComponent: StationaryCombustionDialog,
    closeSimpleDialog: closeStationaryCombustionDialog,
  } = useSimpleDialog();

  const { results: activities, count } = data || {};

  const pageCount = count ? Math.ceil(count / (perPage || 10)) : 0;

  const { timePeriodsFilter, sitesFilter, fuelsFilter } =
    useServerFilterOptions(
      ['timePeriods', 'sites', 'fuels'] as const,
      data?.filters || {}
    );

  const useFilterOptions: FilterOptions<['timePeriods', 'sites', 'fuels']> =
    useMemo(
      () => ({
        timePeriods: timePeriodsFilter,
        sites: sitesFilter,
        fuels: fuelsFilter,
      }),
      [timePeriodsFilter, sitesFilter, fuelsFilter]
    );

  const { table, context } = useServerDataTable({
    data: activities || [],
    columns: useStationaryCombustionTableColumns({
      filters: useFilterOptions,
    }),
    pageCount,
    getRowId: (row) => row.uuid,
    initialState: {
      columnVisibility: {
        uuid: false,
        period: false,
        scope3Emissions: !!scope3Supported,
      },
    },
  });

  return (
    <>
      <DataTable
        table={table}
        context={context}
        className="bg-gray-00"
        onRowClick={(row) => {
          row && setSelectedActivityUuid(row.original.uuid);
          showStationaryCombustionDialog();
        }}
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
        tableLoading={!data && status === 'loading'}
        rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
        columnClassName={{
          scope3Emissions: 'border-l',
        }}
      >
        <DataTableToolbar
          table={table}
          filterOrder={['site', 'fuel', 'period']}
        >
          <StationaryCombustionPreviewSheet />
          <AddDataMenu
            categorySlug="stationary-combustion"
            onAddManually={() => {
              setSelectedActivityUuid(undefined);
              showStationaryCombustionDialog();
            }}
          />
        </DataTableToolbar>
      </DataTable>
      {StationaryCombustionDialog({
        header: 'Add stationary combustion data',
        content: (
          <StationaryCombustionEditForm
            activityUuid={selectedActivityUuid}
            closeDialog={closeStationaryCombustionDialog}
          />
        ),
      })}
    </>
  );
}
